import { useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Link, List,
  ListItemButton, ListItemText, MenuItem, Paper, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, TextField, Typography,
} from '@mui/material';
import { RequirePermission } from '@/components/RequirePermission';
import { GrafikRestauracjiPage } from '@/workspaces/restaurants/GrafikRestauracjiPage';
import SearchIcon from '@mui/icons-material/Search';
import { useQueryClient } from '@tanstack/react-query';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import type { MiastoSzkol, RestauracjaSzkol, SzkolaDoDodania, SzkolaPary } from '@/lib/api/analytics';
import { opisBleduOperacji, powodBleduOdczytu, type OpisBledu } from '@/lib/administracja/komunikaty';
import { useAuth } from '@/lib/auth/AuthContext';
import { formatCount, odmiana } from '@/lib/format';
import {
  odswiezEkranyGrafiku, useAdminSzkolyRestauracji, useAnalyticsApi, useAudytSzkolRestauracji,
} from '@/lib/useAnalytics';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * ADMINISTRACJA → SZKOŁY I RESTAURACJE (OWNER 2026-10-10)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Wyrocznia: panel restauracji → „Szkoły" (lista wybranych + „Szkoły do wybrania" → „Wybierz")
 * i „Usuń" ze starego Grafiku admina. Tu robi to administracja, dla każdej restauracji:
 * MIASTO → RESTAURACJA → SZKOŁY, alfabetycznie na każdym poziomie, miasta z danych.
 *
 * JEDNA RELACJA, DWA EKRANY, DWIE FUNKCJE:
 *   • ten ekran — KTO kogo obsługuje (dodanie / odpięcie pary),
 *   • Grafik    — W JAKIE DNI (zaznaczenia dni pary).
 * Oba czytają to samo wywołanie systemu głównego: nowa para pojawia się w Grafiku od razu
 * (bez dni), odpięta znika z niego od razu. Żadnej kopii relacji w Control Center.
 *
 * DWIE ZAKŁADKI (OWNER 2026-10-18): [RELACJE] — ten ekran bez zmian (relacja szkoła ↔ restauracja);
 * [GRAFIK WIZUALNY] — przywrócony widok z arkusza „Grafik Warszawa”: szkoły, kolorowe skróty restauracji, pon–pt,
 * godziny zamówień, włączanie/wyłączanie restauracji w dniu (ten sam komponent co Administracja → Grafik, ten sam
 * odczyt i zapis systemu głównego). Aktywna zakładka w adresie: `?widok=grafik` (odświeżenie jej nie gubi).
 */

const GRAFIK = '/administracja/szkoly-i-restauracje?widok=grafik';
const WZOR_GODZINY = /^([01]\d|2[0-3]):([0-5]\d)$/;

function bezOgonkow(t: string) {
  return t.toLocaleLowerCase('pl-PL').normalize('NFD').replace(/\p{Diacritic}/gu, '').replace(/ł/g, 'l').trim();
}

/** Najczęstsza godzina zamknięcia w parach restauracji — podpowiedź przy dodawaniu nowej szkoły. */
function typowaGodzina(r: RestauracjaSzkol): string {
  const licznik = new Map<string, number>();
  for (const s of r.szkoly) if (s.zamkniecieZamowien) licznik.set(s.zamkniecieZamowien, (licznik.get(s.zamkniecieZamowien) ?? 0) + 1);
  return [...licznik.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0] ?? '';
}

type Wynik = { rodzaj: 'ok' | 'uwaga'; tekst: string };

// ── DODANIE SZKOŁY ──────────────────────────────────────────────────────────────────────────────

function OknoDodania({ miasto, restauracja, onZamknij, onWynik, onOdswiez }: {
  miasto: MiastoSzkol; restauracja: RestauracjaSzkol; onZamknij: () => void;
  onWynik: (w: Wynik) => Promise<void>; onOdswiez: () => Promise<unknown>;
}) {
  const api = useAnalyticsApi();
  const [szukaj, setSzukaj] = useState('');
  const [wybrana, setWybrana] = useState<SzkolaDoDodania | null>(null);
  const [godzina, setGodzina] = useState(() => typowaGodzina(restauracja));
  const [wysylanie, setWysylanie] = useState(false);
  const [blad, setBlad] = useState<OpisBledu | null>(null);

  const fraza = bezOgonkow(szukaj);
  const lista = restauracja.doDodania.filter((s) => !fraza || bezOgonkow(`${s.nazwa} ${s.adres ?? ''}`).includes(fraza));
  const godzinaPoprawna = WZOR_GODZINY.test(godzina);

  async function dodaj() {
    if (!wybrana || !godzinaPoprawna || wysylanie) return;
    setWysylanie(true);
    setBlad(null);
    try {
      const r = await api.adminPrzypiszSzkole({ restauracjaId: restauracja.id, szkolaId: wybrana.id, godzinaZamkniecia: godzina });
      await onWynik(r.potwierdzono
        ? { rodzaj: 'ok', tekst: `Dodano: ${wybrana.nazwa} → ${restauracja.nazwa} (zamówienia do ${r.para?.zamkniecie_zamowien ?? godzina}). Para nie ma jeszcze dni dostaw — ustaw je w Grafiku.` }
        : { rodzaj: 'uwaga', tekst: `System główny przyjął dodanie ${wybrana.nazwa} → ${restauracja.nazwa}, ale para nie pojawiła się jeszcze w odczycie. Odśwież za chwilę.` });
      onZamknij();
    } catch (e) {
      // Błąd zostaje w oknie (operator może poprawić i spróbować ponownie); widok i tak odczytujemy
      // ponownie — przy wyniku nieznanym para mogła powstać.
      setBlad(opisBleduOperacji(e));
      await onOdswiez();
    } finally {
      setWysylanie(false);
    }
  }

  return (
    <Dialog open onClose={() => (wysylanie ? undefined : onZamknij())} fullWidth maxWidth="sm" aria-labelledby="tytul-dodania">
      <DialogTitle id="tytul-dodania">
        Dodaj szkołę
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{restauracja.nazwa} · {miasto.nazwa}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            size="small"
            label="Szukaj szkoły (nazwa, adres)"
            value={szukaj}
            onChange={(e) => setSzukaj(e.target.value)}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
            autoFocus
          />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Szkoły z miasta {miasto.nazwa}, jeszcze nieprzypisane do tej restauracji (bez usuniętych, niepotwierdzonych
            i zablokowanych) — ta sama lista, którą restauracja widziała w starym panelu. Pokazano {lista.length} z {restauracja.doDodania.length}.
          </Typography>
          {lista.length === 0 ? (
            <Alert severity="info">
              {restauracja.doDodania.length === 0
                ? 'Wszystkie szkoły z tego miasta są już przypisane do tej restauracji.'
                : 'Żadna szkoła nie pasuje do wyszukiwania.'}
            </Alert>
          ) : (
            <Paper variant="outlined" sx={{ maxHeight: 320, overflow: 'auto' }}>
              <List dense disablePadding aria-label="Szkoły do dodania">
                {lista.map((s) => (
                  <ListItemButton key={s.id} selected={wybrana?.id === s.id} onClick={() => setWybrana(s)} data-szkola-do-dodania={s.id}>
                    <ListItemText primary={s.nazwa} secondary={s.adres ?? 'brak adresu w systemie głównym'} />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          )}
          <TextField
            size="small"
            label="Godzina zamknięcia zamówień (GG:MM)"
            value={godzina}
            onChange={(e) => setGodzina(e.target.value.trim())}
            error={godzina !== '' && !godzinaPoprawna}
            helperText={godzina !== '' && !godzinaPoprawna
              ? 'Format GG:MM, np. 10:50.'
              : 'Do której uczniowie tej szkoły zamawiają z tej restauracji. Podpowiedź: najczęstsza godzina w parach tej restauracji.'}
            slotProps={{ htmlInput: { inputMode: 'numeric', autoComplete: 'off' } }}
          />
          {wybrana ? (
            <Alert severity="info" role="status">
              Dodać <strong>{wybrana.nazwa}</strong> do <strong>{restauracja.nazwa}</strong>
              {godzinaPoprawna ? <> z zamknięciem zamówień o <strong>{godzina}</strong></> : null}? Para powstanie bez dni dostaw —
              dni zaznacza się potem w <Link component={RouterLink} to={GRAFIK}>Grafiku</Link>.
            </Alert>
          ) : null}
          {blad ? (
            <Alert severity={blad.wynikNieznany ? 'warning' : 'error'} role="alert">
              {blad.tekst}
              {blad.korelacja ? <Box component="div" sx={{ mt: 0.5, fontSize: 12 }}>Identyfikator zgłoszenia: {blad.korelacja}</Box> : null}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onZamknij} disabled={wysylanie}>Anuluj</Button>
        <Button variant="contained" onClick={() => void dodaj()} disabled={!wybrana || !godzinaPoprawna || wysylanie}>
          {wysylanie ? 'Dodawanie…' : 'Dodaj szkołę'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── ODPIĘCIE SZKOŁY ─────────────────────────────────────────────────────────────────────────────

function OknoOdpiecia({ restauracja, szkola, onZamknij, onWynik, onOdswiez }: {
  restauracja: RestauracjaSzkol; szkola: SzkolaPary; onZamknij: () => void;
  onWynik: (w: Wynik) => Promise<void>; onOdswiez: () => Promise<unknown>;
}) {
  const api = useAnalyticsApi();
  const [wysylanie, setWysylanie] = useState(false);
  const [blad, setBlad] = useState<OpisBledu | null>(null);

  async function odepnij() {
    if (wysylanie) return;
    setWysylanie(true);
    setBlad(null);
    try {
      const r = await api.adminOdepnijSzkole({ restauracjaId: restauracja.id, szkolaId: szkola.id });
      await onWynik(r.odpieto
        ? { rodzaj: 'ok', tekst: `Odpięto: ${szkola.nazwa} od ${restauracja.nazwa}. Grafik nie pokazuje już tej pary.` }
        : { rodzaj: 'uwaga', tekst: `System główny przyjął odpięcie, ale para ${szkola.nazwa} → ${restauracja.nazwa} nadal jest w odczycie. Sprawdź stan.` });
      onZamknij();
    } catch (e) {
      setBlad(opisBleduOperacji(e));
      await onOdswiez();
    } finally {
      setWysylanie(false);
    }
  }

  return (
    <Dialog open onClose={() => (wysylanie ? undefined : onZamknij())} fullWidth maxWidth="sm" aria-labelledby="tytul-odpiecia">
      <DialogTitle id="tytul-odpiecia">Odpiąć szkołę od restauracji?</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography>
            <strong>{szkola.nazwa}</strong> ← <strong>{restauracja.nazwa}</strong>
          </Typography>
          <Typography variant="body2">
            Dni dostaw pon–pt: <strong>{szkola.dniDostaw}</strong> · zamówienia do: <strong>{szkola.zamkniecieZamowien ?? 'bez godziny'}</strong>
          </Typography>
          <Alert severity="warning">
            Ta sama operacja co „Usuń” w starym Grafiku admina: restauracja przestaje być dostępna w tej szkole we wszystkie
            dni, a para znika z Grafiku. Ponowne dodanie szkoły przywraca parę bez dni dostaw.
          </Alert>
          {blad ? (
            <Alert severity={blad.wynikNieznany ? 'warning' : 'error'} role="alert">
              {blad.tekst}
              {blad.korelacja ? <Box component="div" sx={{ mt: 0.5, fontSize: 12 }}>Identyfikator zgłoszenia: {blad.korelacja}</Box> : null}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onZamknij} disabled={wysylanie}>Anuluj</Button>
        <Button variant="contained" color="error" onClick={() => void odepnij()} disabled={wysylanie}>
          {wysylanie ? 'Odpinanie…' : 'Odepnij szkołę'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── EKRAN ───────────────────────────────────────────────────────────────────────────────────────

type Zakladka = 'relacje' | 'grafik';

export function SzkolyIRestauracjePage() {
  const [parametry, setParametry] = useSearchParams();
  const zakladka: Zakladka = parametry.get('widok') === 'grafik' ? 'grafik' : 'relacje';
  const zmien = (z: Zakladka) => {
    const n = new URLSearchParams(parametry);
    if (z === 'grafik') n.set('widok', 'grafik'); else n.delete('widok');
    setParametry(n, { replace: true });
  };

  return (
    <Stack spacing={2.5}>
      <NaglowekEkranu
        tytul="Szkoły i restauracje"
        opis={
          <>
            Dwa sposoby pracy na tej samej relacji z systemu głównego. <b>Relacje</b> — która restauracja obsługuje którą
            szkołę (dodanie jak „Szkoły → Wybierz” w panelu restauracji, odpięcie jak „Usuń” w starym Grafiku).{' '}
            <b>Grafik wizualny</b> — szkoły z kolorowymi skrótami restauracji i szybkie ustawianie dni pon–pt.
          </>
        }
        kartaId="ADMIN-SCHOOLS-00"
      />
      <Tabs value={zakladka} onChange={(_, v: Zakladka) => zmien(v)} aria-label="Widok: relacje albo grafik wizualny"
        sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="relacje" label="Relacje" id="zakladka-relacje" aria-controls="panel-relacje" />
        <Tab value="grafik" label="Grafik wizualny" id="zakladka-grafik" aria-controls="panel-grafik" />
      </Tabs>
      <Box role="tabpanel" id={`panel-${zakladka}`} aria-labelledby={`zakladka-${zakladka}`}>
        {zakladka === 'grafik' ? (
          <RequirePermission permissions={['schedule.read']}>
            <GrafikRestauracjiPage osadzony />
          </RequirePermission>
        ) : <ZakladkaRelacje />}
      </Box>
    </Stack>
  );
}

/** Zakładka „Relacje” — dotychczasowy ekran 1:1 (bez własnego nagłówka: ma go strona z zakładkami). */
function ZakladkaRelacje() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const zapytanie = useAdminSzkolyRestauracji();
  const mozeEdytowac = auth.has('restaurantSchools.write');

  const [szukaj, setSzukaj] = useState('');
  const [filtrMiasta, setFiltrMiasta] = useState<string>('');
  const [dodanie, setDodanie] = useState<{ miasto: MiastoSzkol; restauracja: RestauracjaSzkol } | null>(null);
  const [odpiecie, setOdpiecie] = useState<{ restauracja: RestauracjaSzkol; szkola: SzkolaPary } | null>(null);
  const [wynik, setWynik] = useState<Wynik | null>(null);
  const [pokazAudyt, setPokazAudyt] = useState(false);
  const audyt = useAudytSzkolRestauracji(pokazAudyt);

  const dane = zapytanie.data;
  const fraza = bezOgonkow(szukaj);

  const widok = useMemo(() => {
    if (!dane) return [];
    return dane.miasta
      .filter((m) => filtrMiasta === '' || String(m.id) === filtrMiasta)
      .map((m) => ({
        ...m,
        restauracje: m.restauracje
          .map((r) => {
            if (!fraza || bezOgonkow(r.nazwa).includes(fraza)) return r;
            const szkoly = r.szkoly.filter((s) => bezOgonkow(s.nazwa).includes(fraza));
            return szkoly.length ? { ...r, szkoly } : null;
          })
          .filter((r): r is RestauracjaSzkol => r !== null),
      }))
      .filter((m) => m.restauracje.length > 0);
  }, [dane, filtrMiasta, fraza]);

  // Ten ekran i Grafik czytają tę samą relację — odświeżamy oba (oraz ślad zmian).
  const odswiez = () => odswiezEkranyGrafiku(queryClient);

  async function poZmianie(w: Wynik) {
    setWynik(w);
    await odswiez();
  }

  const naglowek = (
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      Która restauracja obsługuje którą szkołę — relacja z systemu głównego, ta sama, którą czyta Grafik.
      Dni dostaw ustawia się w zakładce <Link component={RouterLink} to={GRAFIK}>Grafik wizualny</Link>.
    </Typography>
  );

  if (zapytanie.isLoading) {
    return <Stack spacing={2}>{naglowek}<LoadingState label="Wczytywanie szkół i restauracji z systemu głównego…" /></Stack>;
  }
  if (zapytanie.error || !dane) {
    return (
      <Stack spacing={2}>
        {naglowek}
        <ErrorState
          error={zapytanie.error}
          context={[powodBleduOdczytu(zapytanie.error), 'Relacja szkoła–restauracja czyta się wyłącznie z systemu głównego — bez kopii zastępczej.'].filter(Boolean).join(' ')}
        />
        <Box><Button onClick={() => void zapytanie.refetch()}>Spróbuj ponownie</Button></Box>
      </Stack>
    );
  }

  const restauracji = dane.miasta.reduce((a, m) => a + m.restauracje.length, 0);
  const par = dane.miasta.reduce((a, m) => a + m.restauracje.reduce((b, r) => b + r.szkoly.length, 0), 0);
  const bezDni = dane.miasta.reduce((a, m) => a + m.restauracje.reduce((b, r) => b + r.szkoly.filter((s) => s.dniDostaw === 0).length, 0), 0);

  return (
    <Stack spacing={2.5}>
      {naglowek}

      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
        {([['miast', dane.miasta.length], ['restauracji', restauracji], ['par szkoła–restauracja', par], ['par bez dni dostaw', bezDni]] as const)
          .map(([etykieta, wartosc]) => (
            <Paper key={etykieta} variant="outlined" sx={{ px: 2, py: 1.25, minWidth: 140 }}>
              <Typography className="sygnal-liczba" sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1 }}>{formatCount(wartosc)}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{etykieta}</Typography>
            </Paper>
          ))}
      </Stack>

      {!dane.zapisPodlaczony ? (
        <Alert severity="info">Zapis nie jest w tym środowisku podłączony do systemu głównego — widok tylko do odczytu.</Alert>
      ) : !mozeEdytowac ? (
        <Alert severity="info">Twoje konto widzi przypisania, ale ich nie zmienia.</Alert>
      ) : null}

      {wynik ? (
        <Alert severity={wynik.rodzaj === 'ok' ? 'success' : 'warning'} onClose={() => setWynik(null)} role="status">
          {wynik.tekst}{' '}
          {wynik.rodzaj === 'ok' ? <Link component={RouterLink} to={GRAFIK}>Przejdź do Grafiku</Link> : null}
        </Alert>
      ) : null}

      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          label="Szukaj restauracji lub szkoły"
          value={szukaj}
          onChange={(e) => setSzukaj(e.target.value)}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
          sx={{ minWidth: { xs: '100%', sm: 300 } }}
        />
        <TextField select size="small" label="Miasto" value={filtrMiasta} onChange={(e) => setFiltrMiasta(e.target.value)} sx={{ minWidth: 200 }}>
          <MenuItem value="">Wszystkie</MenuItem>
          {dane.miasta.map((m) => <MenuItem key={String(m.id)} value={String(m.id)}>{m.nazwa}</MenuItem>)}
        </TextField>
        {szukaj || filtrMiasta ? <Button size="small" onClick={() => { setSzukaj(''); setFiltrMiasta(''); }}>Wyczyść filtry</Button> : null}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Odczytano {new Date(dane.odczytano).toLocaleTimeString('pl-PL', { timeZone: 'Europe/Warsaw' })}
        </Typography>
        <Button size="small" onClick={() => void zapytanie.refetch()} disabled={zapytanie.isFetching}>Odśwież</Button>
      </Stack>

      {widok.length === 0 ? (
        <EmptyState
          title={dane.miasta.length ? 'Nic nie pasuje do filtrów' : 'Brak restauracji w systemie głównym'}
          description={dane.miasta.length ? 'Zmień wyszukiwanie albo wyczyść filtry.' : 'System główny nie zwrócił żadnej aktywnej restauracji.'}
        />
      ) : widok.map((miasto) => (
        <Karta key={String(miasto.id)} nadtytul="Miasto" tytul={miasto.nazwa} kartaId={`ADMIN-SCHOOLS-CITY-${miasto.id ?? 'brak'}`} poziom="h2">
          <Stack spacing={2}>
            {miasto.restauracje.map((r) => {
              const pelna = dane.miasta.find((m) => m.id === miasto.id)?.restauracje.find((x) => x.id === r.id) ?? r;
              return (
                <Paper key={r.id} variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }} data-restauracja-id={r.id}>
                  <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>{r.nazwa}</Typography>
                    <Chip size="small" label={`${pelna.szkoly.length} ${odmiana(pelna.szkoly.length, ['szkoła', 'szkoły', 'szkół'])}`} />
                    <Box sx={{ flexGrow: 1 }} />
                    {mozeEdytowac && dane.zapisPodlaczony ? (
                      <Button size="small" variant="outlined" onClick={() => setDodanie({ miasto, restauracja: pelna })}
                        aria-label={`Dodaj szkołę do ${r.nazwa}`}>
                        + Dodaj szkołę
                      </Button>
                    ) : null}
                  </Stack>
                  {r.szkoly.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Brak przypisanych szkół.</Typography>
                  ) : (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small" aria-label={`Szkoły restauracji ${r.nazwa}`}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Szkoła</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="center">Dni dostaw pon–pt</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Zamówienia do</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Akcje</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {r.szkoly.map((s) => (
                            <TableRow key={s.id} hover data-szkola-id={s.id}>
                              <TableCell>{s.nazwa}</TableCell>
                              <TableCell align="center">
                                {s.dniDostaw === 0 ? (
                                  <Chip size="small" variant="outlined" color="warning" label="brak — ustaw w Grafiku"
                                    component={RouterLink} to={GRAFIK} clickable />
                                ) : s.dniDostaw}
                              </TableCell>
                              <TableCell>
                                {s.zamkniecieZamowien ?? (
                                  <Chip size="small" color="warning" variant="outlined" label="bez godziny"
                                    title="Para ma dni dostaw, ale godziny zamknięcia zamówień nie znamy. Nie zakładamy północy." />
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {mozeEdytowac && dane.zapisPodlaczony ? (
                                  <Button size="small" color="error" onClick={() => setOdpiecie({ restauracja: pelna, szkola: s })}
                                    aria-label={`Odepnij ${s.nazwa} od ${r.nazwa}`}>
                                    Odepnij
                                  </Button>
                                ) : null}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </Karta>
      ))}

      <Box>
        <Button size="small" onClick={() => setPokazAudyt((v) => !v)}>
          {pokazAudyt ? 'Ukryj ostatnie zmiany' : 'Pokaż ostatnie zmiany przypisań'}
        </Button>
        <Collapse in={pokazAudyt} unmountOnExit>
          <Paper variant="outlined" sx={{ p: 2, mt: 1, overflowX: 'auto' }}>
            {audyt.data?.wpisy.length ? (
              <Table size="small" aria-label="Ostatnie zmiany przypisań">
                <TableHead>
                  <TableRow>
                    <TableCell>Kiedy</TableCell><TableCell>Kto</TableCell><TableCell>Operacja</TableCell>
                    <TableCell>Restauracja</TableCell><TableCell>Szkoła</TableCell><TableCell>Wynik</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {audyt.data.wpisy.map((w, i) => (
                    <TableRow key={`${w.czas}-${i}`}>
                      <TableCell>{new Date(w.czas).toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw' })}</TableCell>
                      <TableCell>{w.aktor.email ?? '—'}</TableCell>
                      <TableCell>{w.operacja === 'PRZYPISZ_SZKOLE' ? 'Dodanie szkoły' : 'Odpięcie szkoły'}</TableCell>
                      <TableCell>{String(w.encja.restauracja ?? w.encja.restauracjaId ?? '—')}</TableCell>
                      <TableCell>{String(w.encja.szkola ?? w.encja.szkolaId ?? '—')}</TableCell>
                      <TableCell>{w.wynik}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : audyt.isLoading ? <LoadingState /> : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Brak zmian od uruchomienia usługi.</Typography>
            )}
          </Paper>
        </Collapse>
      </Box>

      {dodanie ? (
        <OknoDodania miasto={dodanie.miasto} restauracja={dodanie.restauracja} onZamknij={() => setDodanie(null)} onWynik={poZmianie} onOdswiez={odswiez} />
      ) : null}
      {odpiecie ? (
        <OknoOdpiecia restauracja={odpiecie.restauracja} szkola={odpiecie.szkola} onZamknij={() => setOdpiecie(null)} onWynik={poZmianie} onOdswiez={odswiez} />
      ) : null}
    </Stack>
  );
}

export default SzkolyIRestauracjePage;
